import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import { ProjectRenderer } from '@/renderer/ProjectRenderer';
import { creatorActions } from '../slices/creatorSlice';
import { RootState, AppDispatch } from '..';
import { clamp } from 'lodash';
import {
	getRequiredMiddlePostsQuantity,
	getEmptyCreatorPost,
	checkAttributeValueAvailability,
	filterVariationAccessoriesByConditions,
	checkMiddlePostLimitReached,
	parseTotalHeightFormula,
} from '@/utils/creatorUtils';
import { getContent } from '@/utils/contentUtils';
import { NotificationID, PostType } from '@/types/creator';
import { ThreeEvent, ThreeEvents } from '@/renderer/types';
import { AttributeTarget } from '@/api/AttributeApi';
import { LedType } from '@/api/AccessoryApi';
import { notifications } from '@mantine/notifications';
import { UUID } from '@/types/types';

const renderer = ProjectRenderer.getInstance();

export const creatorListenerMiddleware = createListenerMiddleware();

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.initialize,
	effect: async ({ payload }, listenerApi) => {
		if (payload.defaultVariation)
			listenerApi.dispatch(
				creatorActions.selectVariation(payload.defaultVariation)
			);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.reset,
	effect: async ({ payload }, listenerApi) => {
		renderer.clear();
		notifications.clean();
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.loadProduct,
	effect: async ({ payload }, listenerApi) => {
		const creatorState = listenerApi.getState().creator;

		await renderer.load(
			payload.product.serializedCreator.exportedRenderer,
			creatorState.creatorAccessories
		);

		const actions = Object.values(creatorState.selectedValues)
			.filter((value) => !!value)
			.map((value) => value.actions)
			.flat();

		for (const creatorPost of creatorState.creatorPosts) {
			actions.push(
				...Object.values(creatorPost.selectedValues)
					.filter((value) => !!value)
					.map((value) => value.actions)
					.flat()
					.map((action) => ({
						...action,
						isPostConfiguratorAction: true,
						payload: {
							...action.payload,
							target: creatorPost.id,
						},
					}))
			);
		}

		for (const [key, creatorEnding] of Object.entries(
			creatorState.creatorEndings
		)) {
			actions.push(
				...Object.values(creatorEnding.selectedValues)
					.filter((value) => !!value)
					.map((value) => value.actions)
					.flat()
					.map((action) => ({
						...action,
						payload: {
							...action.payload,
							target: key,
						},
					}))
			);
		}

		for (const creatorAccessory of creatorState.creatorAccessories) {
			actions.push(
				...Object.values(creatorAccessory.selectedValues)
					.filter((value) => !!value)
					.map((value) => value.actions)
					.flat()
			);
		}

		renderer.handleActions(actions);

		if (creatorState.options.endingsConfiguration.lowerEnding)
			renderer.handleManager.setEndingVisibility({
				type: 'lowerEnding',
				value: true,
			});
		if (creatorState.options.endingsConfiguration.upperEnding)
			renderer.handleManager.setEndingVisibility({
				type: 'upperEnding',
				value: true,
			});

		listenerApi.dispatch(
			creatorActions.setTotalHeight(
				parseTotalHeightFormula(listenerApi.getState().creator)
			)
		);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.handleRendererEvent,
	effect: ({ payload }, listenerApi) => {
		const state = listenerApi.getState().creator;

		switch (payload.type) {
			case ThreeEvent.ADD_POST: {
				const data = payload.data as ThreeEvents[ThreeEvent.ADD_POST];

				const post = getEmptyCreatorPost(data.type, data.posY);
				if ([PostType.UPPER, PostType.LOWER].includes(data.type))
					post.isAdjustable = !!state.productTypeVariation?.sidePostsAdjustable;

				listenerApi.dispatch(creatorActions.addPost(post));

				break;
			}
			case ThreeEvent.DETECTED_COLLISION: {
				if (state.options.collisionDetected)
					notifications.show({
						id: NotificationID.COLLISION_DETECTED,
						color: 'red',
						title: getContent('notifications.collisionTitle'),
						message: getContent('notifications.collisionMessage'),
						autoClose: false,
					});
				else notifications.hide(NotificationID.COLLISION_DETECTED);
			}
		}
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.setCreatorStep,
	effect: ({ payload }, listenerApi) => {
		renderer.handleCreatorStepChange(payload);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.setTotalHeight,
	effect: ({ payload }) => {
		renderer.handleManager.setTotalHeight(payload / 1000);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.selectVariation,
	effect: ({ payload }, listenerApi) => {
		if (!payload.model3d.model) throw new Error('MISSING_MODEL_3D');
		renderer.handleSelectVariation(payload.model3d.model);
		renderer.objectsManager.sidePostsAdjustable = payload.sidePostsAdjustable;
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.selectProductAttributeValue,
	effect: ({ payload }, listenerApi) => {
		if (payload.attributeValue) {
			const actions = payload.attributeValue.actions;
			renderer.handleActions(actions);
		}

		const { selectedValues, availableAttributeValues } =
			listenerApi.getState().creator;

		const chosenValues = Object.values(selectedValues).filter(
			(value) => !!value
		);

		for (const selectedValue of chosenValues) {
			if (!selectedValue.relations.length) continue;

			const isAvailabile = checkAttributeValueAvailability(
				selectedValue,
				chosenValues
			);
			if (isAvailabile) continue;

			const attributeValues = availableAttributeValues[
				AttributeTarget.HANDLE
			].find(
				(attribute) => attribute.id === selectedValue.attributeId
			)?.attributeValues;

			const availableAttributeValue = attributeValues?.find((attributeValue) =>
				checkAttributeValueAvailability(attributeValue, chosenValues)
			);
			if (!availableAttributeValue) continue;

			listenerApi.dispatch(
				creatorActions.selectProductAttributeValue({
					attributeId: selectedValue.attributeId,
					attributeValue: availableAttributeValue,
				})
			);
		}
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.selectPostAttributeValue,
	effect: ({ payload }, listenerApi) => {
		const actions = payload.attributeValue.actions.map((action) => ({
			...action,
			isPostConfiguratorAction: true,
			payload: {
				...action.payload,
				target: payload.postId,
			},
		}));

		renderer.handleActions(actions);

		const { creatorPosts, availableAttributeValues } =
			listenerApi.getState().creator;

		const post = creatorPosts.find((post) => post.id === payload.postId);
		if (!post) return;

		const chosenValues = Object.values(post.selectedValues).filter(
			(value) => !!value
		);

		for (const selectedValue of chosenValues) {
			if (!selectedValue.relations.length) continue;

			const isAvailabile = checkAttributeValueAvailability(
				selectedValue,
				chosenValues
			);
			if (isAvailabile) continue;

			const attributeValues = availableAttributeValues[
				AttributeTarget.POSTS
			].find(
				(attribute) => attribute.id === selectedValue.attributeId
			)?.attributeValues;

			const availableAttributeValue = attributeValues?.find((attributeValue) =>
				checkAttributeValueAvailability(attributeValue, chosenValues)
			);
			if (!availableAttributeValue) continue;

			listenerApi.dispatch(
				creatorActions.selectPostAttributeValue({
					postId: post.id,
					attributeId: availableAttributeValue.attributeId,
					attributeValue: availableAttributeValue,
				})
			);
		}
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.setEndingConfiguration,
	effect: ({ payload }, listenerApi) => {
		renderer.handleManager.setEndingVisibility(payload);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.selectEndingAttributeValue,
	effect: ({ payload }, listenerApi) => {
		const actions = payload.attributeValue.actions.map((action) => ({
			...action,
			payload: {
				...action.payload,
				target: payload.type,
			},
		}));

		renderer.handleActions(actions);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.addPost,
	effect: ({ payload }, listenerApi) => {
		const state = listenerApi.getState().creator;

		checkMiddlePostLimitReached(state);

		renderer.objectsManager.addPost(
			payload,
			payload.isAutoAdded && state.options.autoMiddlePostPositioning
		);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.removePost,
	effect: ({ payload }, listenerApi) => {
		const state = listenerApi.getState().creator;

		checkMiddlePostLimitReached(state);

		renderer.objectsManager.removePost(
			payload.id,
			payload.type === PostType.MIDDLE &&
				state.options.autoMiddlePostPositioning
		);

		if (!payload.linkedCreatorAccessoryId) return;
		const accessory = listenerApi
			.getState()
			.creator.creatorAccessories.find(
				(a) => a.refId === payload.linkedCreatorAccessoryId
			);
		if (!accessory) return;

		listenerApi.dispatch(
			creatorActions.requestRemoveAccessory(accessory.refId)
		);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.setPostPosition,
	effect: ({ payload }, listenerApi) => {
		const post = listenerApi
			.getState()
			.creator.creatorPosts.find((post) => post.id === payload.id)!;

		renderer.objectsManager.setPostPosY(post.id, payload.posY);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.updateHeights,
	effect: ({ payload }, listenerApi) => {
		const { productTypeVariation, productType } =
			listenerApi.getState().creator;
		if (!productTypeVariation || !productType) return;

		if (payload.handle !== undefined) {
			const posts = listenerApi.getState().creator.creatorPosts;

			const middlePosts = posts.filter((post) => post.type === PostType.MIDDLE),
				autoAddedPosts = middlePosts.filter((post) => post.isAutoAdded);

			const length = clamp(
				payload.handle,
				productTypeVariation.lengthSettings.handle.min,
				productTypeVariation.lengthSettings.handle.max
			);

			renderer.handleManager.updateHeights({
				handle: length / 1000,
			});

			if (!productTypeVariation.withMiddlePost) return;

			const requiredMiddlePostsQuantity = getRequiredMiddlePostsQuantity(
					length,
					productTypeVariation.autoMiddlePostThreshold
				),
				addDiff = requiredMiddlePostsQuantity - middlePosts.length,
				removeDiff = autoAddedPosts.length - requiredMiddlePostsQuantity;

			for (let index = 0; index < addDiff; index++) {
				const newPost = getEmptyCreatorPost(PostType.MIDDLE);
				newPost.isAutoAdded = true;

				listenerApi.dispatch(creatorActions.addPost(newPost));
			}

			for (let index = 0; index < removeDiff; index++) {
				const autoPost = autoAddedPosts[index];
				if (!autoPost) continue;

				listenerApi.dispatch(creatorActions.removePost(autoPost));
			}

			return;
		}

		if (
			payload.upperEnding !== undefined ||
			payload.lowerEnding !== undefined
		) {
			let key: keyof typeof payload;
			for (key in payload) {
				payload[key] =
					clamp(
						payload[key]!,
						productTypeVariation.lengthSettings.endings.min,
						productTypeVariation.lengthSettings.endings.max
					) / 1000;
			}
		}

		renderer.handleManager.updateHeights(payload);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	predicate: (action) =>
		isAnyOf(
			creatorActions.updateHeights,
			creatorActions.setEndingConfiguration
		)(action) ||
		(creatorActions.handleRendererEvent.match(action) &&
			action.payload.type === ThreeEvent.SET_ADJUSTABLE_HEIGHTS),
	effect: (_, listenerApi) => {
		listenerApi.dispatch(
			creatorActions.setTotalHeight(
				parseTotalHeightFormula(listenerApi.getState().creator)
			)
		);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.addCreatorAccessory,
	effect: async ({ payload }, listenerApi) => {
		if (!payload.variationAccessory.accessory.model3d) return;

		const state = listenerApi.getState().creator;

		const productType = state.productType;
		if (!productType) return;

		for (const creatorAccessory of state.creatorAccessories) {
			if (!payload.parentCreatorAccessoryRefId) break;

			if (
				creatorAccessory.parentCreatorAccessoryRefId !==
				payload.parentCreatorAccessoryRefId
			)
				continue;
			if (creatorAccessory.refId === payload.refId) continue;

			listenerApi.dispatch(
				creatorActions.removeCreatorAccessories([creatorAccessory.refId])
			);
		}

		switch (payload.variationAccessory.accessory.ledType) {
			case LedType.LED_TAPE:
				renderer.objectsManager.ledsManager.initLedTapes(payload);
				break;
			case LedType.LED_POINT:
				renderer.objectsManager.ledsManager.addLedPoint(payload);
				break;
			default:
				await renderer.objectsManager.addAccessory(payload);
				if (payload.linkedMiddlePostId) {
					const post = listenerApi
						.getState()
						.creator.creatorPosts.find(
							(p) => p.id === payload.linkedMiddlePostId
						);
					if (post)
						listenerApi.dispatch(
							creatorActions.updateCreatorAccessoryData({
								refId: payload.refId,
								data: {
									posY: post.posY,
								},
							})
						);
				}
		}
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.updateCreatorAccessoryData,
	effect: ({ payload }, listenerApi) => {
		const { refId: id, data } = payload;

		if (data.mountingSide !== undefined) {
			renderer.objectsManager.updateAccessoryMountingSide(
				id,
				data.mountingSide
			);
			return;
		}

		if (data.posY !== undefined) {
			renderer.objectsManager.setAccessoryPosY(
				id,
				data.posY,
				payload.ledType === LedType.LED_POINT
			);
			return;
		}
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.requestRemoveAccessory,
	effect: ({ payload }, listenerApi) => {
		const state = listenerApi.getState().creator;

		const toRemove = new Set<UUID>();

		if (payload) toRemove.add(payload);

		let changed = true;

		while (changed) {
			changed = false;

			const filteredIds = filterVariationAccessoriesByConditions(
				Object.values(state.availableAccessories).flat(),
				{
					...state,
					creatorAccessories: state.creatorAccessories.filter(
						(a) => !toRemove.has(a.refId)
					),
				}
			).map((va) => va.accessory.id);

			for (const acc of state.creatorAccessories) {
				if (
					!filteredIds.includes(acc.variationAccessory.accessory.id) &&
					!toRemove.has(acc.refId)
				) {
					toRemove.add(acc.refId);
					changed = true;
				}
			}
		}

		toRemove.forEach((refId) => {
			const acc = state.creatorAccessories.find((a) => a.refId === refId);
			if (!acc) return;

			if (acc.linkedMiddlePostId) {
				const post = state.creatorPosts.find(
					(p) => p.id === acc.linkedMiddlePostId
				);
				if (post)
					listenerApi.dispatch(
						creatorActions.setPostLinkedAccessory({
							postId: post.id,
							linkedCreatorAccessoryId: null,
						})
					);
			}
		});

		listenerApi.dispatch(
			creatorActions.removeCreatorAccessories([...toRemove])
		);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.removeCreatorAccessories,
	effect: ({ payload }, listenerApi) => {
		const { options } = listenerApi.getState().creator;
		renderer.handleActions(options.pendingRendererActions);

		const { creatorAccessories, creatorPosts } =
			listenerApi.getOriginalState().creator;

		for (const creatorAccessory of creatorAccessories) {
			if (!payload.includes(creatorAccessory.refId)) continue;

			if (!creatorAccessory.linkedMiddlePostId) return;
			const middlePost = creatorPosts.find(
				(post) => post.id === creatorAccessory.linkedMiddlePostId
			);
			if (!middlePost) return;
			if (middlePost.isAutoAdded) return;

			listenerApi.dispatch(creatorActions.removePost(middlePost));
		}

		listenerApi.dispatch(
			creatorActions.updateOptions({
				pendingRendererActions: [],
			})
		);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.removeCreatorAccessories,
	effect: ({ payload }, api) => {
		const prev = api.getOriginalState().creator.creatorAccessories;
		const next = api.getState().creator.creatorAccessories;

		const removed = prev.filter((p) => !next.some((n) => n.refId === p.refId));

		for (const acc of removed) {
			switch (acc.variationAccessory.accessory.ledType) {
				case LedType.NONE:
					renderer.objectsManager.removeAccessory(acc.refId);
					break;
				case LedType.LED_TAPE:
					renderer.objectsManager.ledsManager.removeLedTapes();
					break;
				case LedType.LED_POINT:
					renderer.objectsManager.ledsManager.removeLedPoints();
					break;
			}
		}
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.removeLedPoint,
	effect: ({ payload }, listenerApi) => {
		renderer.objectsManager.ledsManager.removeLedPoint(payload);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.setSelectedDoor,
	effect: ({ payload }, listenerApi) => {
		renderer.doorManager.selectedDoor = payload;
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.toggleLedPointsAutoPositioning,
	effect: ({ payload }, listenerApi) => {
		renderer.objectsManager.ledsManager.autoPositioning = payload;
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.selectAccessoryAttributeValue,
	effect: ({ payload }, listenerApi) => {
		if (payload.attributeValue) {
			const actions = payload.attributeValue.actions;
			renderer.handleActions(actions, false);
		}

		const { creatorAccessories } = listenerApi.getState().creator;

		const filteredCreatorAccessories = creatorAccessories.filter((creaAcc) =>
			payload.refIds.includes(creaAcc.refId)
		);
		if (!filteredCreatorAccessories[0]) return;

		const chosenValues = Object.values(
			filteredCreatorAccessories[0].selectedValues
		).filter((value) => !!value);

		for (const selectedValue of chosenValues) {
			if (!selectedValue.relations.length) continue;

			const isAvailabile = checkAttributeValueAvailability(
				selectedValue,
				chosenValues
			);
			if (isAvailabile) continue;

			const attributeValues =
				filteredCreatorAccessories[0].availableAttributes.find(
					(attribute) => attribute.id === selectedValue.attributeId
				)?.attributeValues;

			const availableAttributeValue = attributeValues?.find((attributeValue) =>
				checkAttributeValueAvailability(attributeValue, chosenValues)
			);
			if (!availableAttributeValue) continue;

			listenerApi.dispatch(
				creatorActions.selectAccessoryAttributeValue({
					refIds: payload.refIds,
					attributeId: availableAttributeValue.attributeId,
					attributeValue: availableAttributeValue,
				})
			);
		}
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.changeCustomValue,
	effect: async ({ payload }, listenerApi) => {
		listenerApi.cancelActiveListeners();
		await listenerApi.delay(500);

		listenerApi.dispatch(creatorActions.setStepsAvailability());
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.setPostLinkedAccessory,
	effect: async ({ payload }, listenerApi) => {
		renderer.objectsManager.setPostLinkedObject(
			payload.postId,
			payload.linkedCreatorAccessoryId
		);
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.setAutoMiddlePostPositioning,
	effect: async ({ payload }, listenerApi) => {
		if (payload) renderer.objectsManager.autoPositionMiddlePosts();
	},
});

creatorListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.toggleMeasurementVisible,
	effect: ({ payload }, listenerApi) => {
		const visibility =
			listenerApi.getState().creator.options.measurementVisible;
		renderer.handleBuilder.measurementManager.visible = visibility;
	},
});
